import { useIsMobile } from "@/lib/useDeviceType";
import React, { useMemo } from "react";
import { Platform, Pressable, View } from "react-native";
import { Popover as TamaguiPopover } from "tamagui";

interface PopoverProps {
  children: React.ReactNode;
  content: React.ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  placement?:
    | "top"
    | "bottom"
    | "left"
    | "right"
    | "top-start"
    | "top-end"
    | "bottom-start"
    | "bottom-end";
  offset?: number;
  width?: number;
  maxWidth?: number;
  maxHeight?: number;
  onMobileOpen?: () => void; // for mobile, if you want to open a sheet instead
  contentBackgroundColor?: string;
  /** Frame only: no fill/shadow; use when `content` provides exact design (e.g. token shadow, radius). */
  bareContent?: boolean;
  /** Popover content corner radius; default 12. */
  contentBorderRadius?: number;
  /**
   * @tamagui `stayInFrame` (shift). Wide content often gets nudged into the viewport and looks
   * “right-aligned” under a left trigger. `false` keeps `placement` (e.g. `bottom-start`).
   */
  stayInFrame?: boolean | { padding?: number };
  /** @see @tamagui/popper `allowFlip` */
  allowFlip?: boolean;
}

export function Popover({
  children,
  content,
  open,
  onOpenChange,
  placement = "bottom-end",
  offset = 8,
  width = 280,
  maxWidth = 280,
  maxHeight = 400,
  onMobileOpen,
  contentBackgroundColor = 'white',
  bareContent = false,
  contentBorderRadius = 12,
  stayInFrame: stayInFrameProp = true,
  allowFlip: allowFlipProp = true,
}: PopoverProps) {
  const isMobile = useIsMobile();

  const handleOpen = useMemo(
    () => () => {
      // if mobile and has onMobileOpen, use that callback
      if (isMobile && onMobileOpen) {
        onMobileOpen();
      } else {
        // just open popover normally
        onOpenChange(true);
      }
    },
    [onOpenChange, isMobile, onMobileOpen],
  );

  // Mobile: sheet/drawer via onMobileOpen
  if (isMobile && onMobileOpen) {
    return (
      <Pressable onPress={handleOpen} hitSlop={8}>
        <View pointerEvents="none">{children}</View>
      </Pressable>
    );
  }

  const trigger =
    Platform.OS !== "web" ? (
      <Pressable hitSlop={8}>
        <View pointerEvents="none">{children}</View>
      </Pressable>
    ) : (
      children
    );

  return (
    <TamaguiPopover
      size="$5"
      allowFlip={allowFlipProp}
      stayInFrame={stayInFrameProp}
      offset={offset}
      placement={placement}
      open={open}
      onOpenChange={onOpenChange}
    >
      {Platform.OS !== "web" ? (
        <TamaguiPopover.Trigger asChild>{trigger}</TamaguiPopover.Trigger>
      ) : (
        <TamaguiPopover.Trigger>{children}</TamaguiPopover.Trigger>
      )}

      <TamaguiPopover.Content
        borderWidth={0}
        borderColor={bareContent ? "transparent" : contentBackgroundColor}
        backgroundColor={bareContent ? "transparent" : contentBackgroundColor}
        padding={0}
        width={width}
        maxWidth={maxWidth}
        maxHeight={maxHeight}
        borderRadius={bareContent ? 0 : contentBorderRadius}
        overflow={bareContent ? "visible" : "hidden"}
        enterStyle={{ y: -10, opacity: 0 }}
        exitStyle={{ y: -10, opacity: 0 }}
        elevate={!bareContent}
        shadowColor={bareContent ? "transparent" : "#000"}
        shadowOffset={bareContent ? { width: 0, height: 0 } : { width: 0, height: 4 }}
        shadowOpacity={bareContent ? 0 : 0.25}
        shadowRadius={bareContent ? 0 : 16}
        zIndex={999}
        animation={[
          "quick",
          {
            opacity: {
              overshootClamping: true,
            },
          },
        ]}
      >
        <TamaguiPopover.Arrow
          borderWidth={0}
          borderColor="transparent"
          backgroundColor="transparent"
          shadowColor="transparent"
          shadowOffset={{ width: 0, height: 4 }}
          shadowOpacity={0}
          shadowRadius={0}
          borderRadius={2}
        />
        {content}
      </TamaguiPopover.Content>
    </TamaguiPopover>
  );
}
